import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import toast from "react-hot-toast";
import "./AdminUsersPage.css";
import Loader from "../layouts/components/Loader";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFilters, setSearchFilters] = useState({
    fname: "",
    lname: ""
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removingAdmin, setRemovingAdmin] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    const userRole = localStorage.getItem("user_role");

    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    // For now, we'll show a message that user search is available
    // In a real implementation, you might want to fetch all users or implement pagination
    setLoading(false);
  }, [navigate]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpenRemoveAdminModal = (user) => {
    if (!user) return;
    setSelectedUser(user);
    setShowRemoveModal(true);
  };

  const removeAdmin = async () => {
    if (!selectedUser) return;

    const token = localStorage.getItem("access_token") || localStorage.getItem("token");

    try {
      setRemovingAdmin(true);
      const response = await fetch(`${API_BASE}/users/${selectedUser.id}/remove-admin`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      const responseData = await response.json().catch(() => ({}));

      if (response.ok) {
        toast.success(
          responseData.message ||
          `Admin role removed. ${selectedUser.fname} ${selectedUser.lname} is now a normal user.`
        );

        setShowRemoveModal(false);

        // Update local users list (use returned user if provided, otherwise set role_id to 2)
        setUsers((prev) =>
          prev.map((u) =>
            u.id === selectedUser.id
              ? (responseData.user ? responseData.user : { ...u, role_id: 2 })
              : u
          )
        );
      } else {
        toast.error(responseData.message || 'Failed to remove admin role');
      }
    } catch (error) {
      console.error('Error removing admin role:', error);
      toast.error('Error removing admin role');
    } finally {
      setRemovingAdmin(false);
    }
  };

  const searchUsers = async () => {
    if (!searchFilters.fname && !searchFilters.lname) {
      toast.error("Please enter at least first name or last name to search");
      return;
    }

    if (!searchFilters.lname) {
      toast.error("Last name is required for user search");
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");

    try {
      const queryParams = new URLSearchParams();
      if (searchFilters.fname) queryParams.append('fname', searchFilters.fname);
      if (searchFilters.lname) queryParams.append('lname', searchFilters.lname);

      const response = await fetch(`${API_BASE}/users/search-by-name?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Handle the specific API response structure for users
        if (data.user) {
          // API returns a single user object, wrap it in an array for display
          setUsers([data.user]);
        } else if (data.users && Array.isArray(data.users)) {
          setUsers(data.users);
        } else if (Array.isArray(data.data)) {
          setUsers(data.data);
        } else if (Array.isArray(data)) {
          setUsers(data);
        } else {
          setUsers([]);
        }
      } else {
        const errorData = await response.json();
        if (response.status === 404) {
          toast.info(errorData.message || "No user found with the given criteria");
          setUsers([]);
        } else {
          toast.error(errorData.message || "Failed to search users");
        }
      }
    } catch (error) {
      toast.error("Error searching users");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchFilters({ fname: "", lname: "" });
    setUsers([]);
  };

  const handlePromoteUser = (user) => {
    setSelectedUser(user);
    setShowPromoteModal(true);
  };

  const promoteUser = async () => {
    if (!selectedUser) return;

    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    
    // Debug: Log the token and user info
    console.log("Token:", token);
    console.log("Current user role:", localStorage.getItem("user_role"));
    console.log("Selected user to promote:", selectedUser);

    try {
      const response = await fetch(`${API_BASE}/users/${selectedUser.id}/promote`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      console.log("Response status:", response.status);
      const responseData = await response.json();
      console.log("Response data:", responseData);

      if (response.ok) {
        toast.success(`User ${selectedUser.fname} ${selectedUser.lname} has been promoted to admin`);
        setShowPromoteModal(false);
        setSelectedUser(null);
        // Refresh the search results
        searchUsers();
      } else {
        toast.error(responseData.message || "Failed to promote user");
      }
    } catch (error) {
      toast.error("Error promoting user");
      console.error("Error:", error);
    }
  };

  const getUserRoleBadge = (roleId) => {
    if (roleId === "1" || roleId === 1) {
      return <span className="role-badge admin">Admin</span>;
    }
    return <span className="role-badge user">User</span>;
  };

  const getGenderBadge = (gender) => {
    const genderClasses = {
      male: "gender-male",
      female: "gender-female",
      other: "gender-other"
    };
    
    return (
      <span className={`gender-badge ${genderClasses[gender] || 'gender-other'}`}>
        {gender ? gender.charAt(0).toUpperCase() + gender.slice(1) : 'N/A'}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-users">
          <div className="loading">Searching users...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-users">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Users Management</h1>
            <p>Search and manage user accounts</p>
          </div>
        </div>

        {/* Search Filters */}
        <div className="search-container">
          <div className="search-filters">
            <div className="filter-group">
              <label htmlFor="fname">First Name</label>
              <input
                type="text"
                id="fname"
                name="fname"
                value={searchFilters.fname}
                onChange={handleSearchChange}
                placeholder="Enter first name"
              />
            </div>

             <div className="filter-group">
               <label htmlFor="lname">Last Name *</label>
               <input
                 type="text"
                 id="lname"
                 name="lname"
                 value={searchFilters.lname}
                 onChange={handleSearchChange}
                 placeholder="Enter last name"
                 required
               />
             </div>
          </div>

          <div className="search-actions">
            <button className="btn-secondary" onClick={clearSearch}>
              Clear
            </button>
            <button className="btn-primary" onClick={searchUsers}>
              <svg viewBox="0 0 24 24">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" fill="none" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Search Users
            </button>
          </div>
        </div>

        {/* Users Table */}
        {users.length > 0 && (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Username</th>
                  <th>Gender</th>
                  <th>Role</th>
                  <th>Birth Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <strong>#{user.id}</strong>
                    </td>
                    <td>
                      <div className="user-name">
                        {user.fname} {user.lname}
                      </div>
                    </td>
                    <td>
                      <div className="user-email">
                        {user.email}
                      </div>
                    </td>
                    <td>
                      <div className="user-username">
                        {user.username}
                      </div>
                    </td>
                    <td>
                      {getGenderBadge(user.gender)}
                    </td>
                    <td>
                      {getUserRoleBadge(user.role_id)}
                    </td>
                    <td>
                      {user.birthdate ? new Date(user.birthdate).toLocaleDateString() : 'N/A'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {user.role_id !== "1" && user.role_id !== 1 && (
                          <button 
                            className="btn-promote"
                            onClick={() => handlePromoteUser(user)}
                            title="Promote to Admin"
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                            </svg>
                            Promote
                          </button>
                        )}
                        {(user.role_id === "1" || user.role_id === 1) && (
                          <button
                            className="btn-warning"
                            onClick={() => handleOpenRemoveAdminModal(user)}
                            title="Remove admin role"
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M3 6h18M9 6v12m6-12v12M5 6l1-3h12l1 3" fill="none" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            Remove Admin
                          </button>
                        )}
                        <button 
                          className="btn-view"
                          title="View Profile"
                        >
                          <svg viewBox="0 0 24 24">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" fill="none" stroke="currentColor" strokeWidth="2"/>
                            <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {users.length === 0 && !loading && (
          <div className="empty-state">
            <div className="empty-icon">
              <svg viewBox="0 0 24 24">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2"/>
                <circle cx="9" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" fill="none" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
             <h3>Search for Users</h3>
             <p>Use the search form above to find users by their first name and last name (last name is required)</p>
          </div>
        )}

        {/* Promote User Modal */}
        {showPromoteModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowPromoteModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Promote User to Admin</h2>
                <button className="modal-close" onClick={() => setShowPromoteModal(false)}>
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>

              <div className="modal-form">
                <div className="promote-warning">
                  <div className="warning-icon">
                    <svg viewBox="0 0 24 24">
                      <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" fill="none" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div className="warning-content">
                    <h3>Are you sure you want to promote this user?</h3>
                    <p>
                      <strong>{selectedUser.fname} {selectedUser.lname}</strong> will be granted admin privileges and will have access to the admin dashboard.
                    </p>
                    <div className="user-details">
                      <p><strong>Email:</strong> {selectedUser.email}</p>
                      <p><strong>Username:</strong> {selectedUser.username}</p>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowPromoteModal(false)}>
                    Cancel
                  </button>
                  <button type="button" className="btn-warning" onClick={promoteUser}>
                    <svg viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                    </svg>
                    Promote to Admin
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remove Admin Modal */}
        {showRemoveModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowRemoveModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Remove Admin Role</h2>
                <button className="modal-close" onClick={() => setShowRemoveModal(false)}>
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>

              <div className="modal-form">
                <div className="promote-warning">
                  <div className="warning-icon">
                    <svg viewBox="0 0 24 24">
                      <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" fill="none" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div className="warning-content">
                    <h3>Are you sure you want to remove admin from this user?</h3>
                    <p>
                      <strong>{selectedUser.fname} {selectedUser.lname}</strong> will lose admin privileges and will become a normal user.
                    </p>
                    <div className="user-details">
                      <p><strong>Email:</strong> {selectedUser.email}</p>
                      <p><strong>Username:</strong> {selectedUser.username}</p>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowRemoveModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-warning"
                    onClick={removeAdmin}
                    disabled={removingAdmin}
                  >
                    {removingAdmin ? (
                      <span><Loader/></span>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24">
                          <path d="M3 6h18M9 6v12m6-12v12M5 6l1-3h12l1 3" fill="none" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Remove Admin
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
